import { CarrierDto, KnownProjectType } from '@groundfloor-platform/mgmt-service-g-sdk';
import { Permissions } from '../../../../core/permissions/permissions.typings';
import { ListPageHeaderProps } from '../../../../ui/components/pure/page-headers/list/list-page-header.component';
import {
  CARRIER_REVIEW_ALL,
  CARRIER_REVIEW_FLAGGED,
  CARRIER_REVIEW_UNFLAGGED,
  CARRIER_STATUS_ACTIVE,
  CARRIER_STATUS_ALL,
  CarriersListControlsState
} from './carriers-list-query.map';

export function makeCarriersListPageHeaderProps(
  projectID: string,
  carriersCount: number,
  form: CarriersListControlsState,
  onFormInput: (update: Partial<CarriersListControlsState>) => void,
  onAdd: () => void,
  title = 'Carriers'
): ListPageHeaderProps<CarriersListControlsState> {
  return {
    title,
    subtitle: `${carriersCount} ${carriersCount === 1 ? 'Carrier' : 'Carriers'}`,
    controls: {
      form,
      onFormInput,
      inputs: [
        {
          type: 'select',
          label: '',
          field: 'status',
          options: CarrierStatusOptions,
          isAdvanced: true,
          halfWidthOnMobile: true
        },
        {
          type: 'select',
          label: '',
          field: 'review',
          options: CarrierReviewOptions,
          isAdvanced: true,
          halfWidthOnMobile: true
        },
        {
          // Name only — the fulltext index does not cover short names.
          type: 'text',
          label: 'Search by name',
          field: 'searchTerm'
        }
      ],
      singleAction: {
        label: 'Add new',
        onClick: onAdd,
        permission: { action: Permissions.groups.create, projectID }
      }
    }
  };
}

// `includeDeleted` is a boolean, so there is no "Deleted only" to offer.
const CarrierStatusOptions = [
  { label: 'Active Only', value: CARRIER_STATUS_ACTIVE },
  { label: 'Active & Deleted', value: CARRIER_STATUS_ALL }
];

const CarrierReviewOptions = [
  { label: 'Approved & Needs Review', value: CARRIER_REVIEW_ALL },
  { label: 'Needs Review Only', value: CARRIER_REVIEW_FLAGGED },
  { label: 'Approved Only', value: CARRIER_REVIEW_UNFLAGGED }
];

/**
 * Three cases, because the advice differs. A search matched nothing is not the project being
 * empty, so it is never told to switch project; widening the filter is offered before switching,
 * being the cheaper thing to try.
 */
export function makeCarriersEmptyMessage(form: CarriersListControlsState): string {
  if (form.searchTerm) {
    return `No carriers match “${form.searchTerm}”. Carriers are searched by name only.`;
  }

  if (form.review === CARRIER_REVIEW_FLAGGED) {
    return 'No carriers here need review.';
  }

  if (form.status === CARRIER_STATUS_ACTIVE) {
    return 'No active carriers service this project. Try including deleted carriers, or choose another project.';
  }

  return 'No carriers service this project. Choose another project to manage its carriers.';
}

/**
 * The carrier project whose couriers list a row opens instead of the carrier detail page, by
 * shortcode where it has one. A company's carrier is listed as the company's top-level group, which
 * the carrier endpoint refuses — its couriers are worked in the company's own project. Undefined
 * for a carrier with no managing project, or one a network or region manages: those open the detail page.
 */
export function carrierCouriersProjectID(carrier: Pick<CarrierDto, 'managedBy'>): string | undefined {
  const { managedBy } = carrier;
  if (managedBy?.type !== KnownProjectType.Carrier) {
    return undefined;
  }

  return managedBy.shortcode || managedBy.id;
}
